import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, ShieldAlert, CheckCircle2, XCircle, Loader2, Globe } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function Ingestion() {
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState("");
  const [expireDays, setExpireDays] = useState(30);
  const [feedUrl, setFeedUrl] = useState("");
  const { user } = useAuth();

  const load = () => {
    api.get("/admin/ingestion").then((r) => {
      setData(r.data);
      setExpireDays(r.data.auto_expire_days ?? 30);
      const pf = (r.data.connectors || []).find((c) => c.name === "partner_feed");
      setFeedUrl(pf?.config?.feed_url || "");
    }).catch((e) => toast.error(fmtErr(e)));
    api.get("/admin/pm-submissions").then((r) => setSubs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveDays = async () => {
    try { await api.put("/admin/settings", { auto_expire_days: expireDays }); toast.success(`Auto-expire window set to ${expireDays} days`); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const saveFeedUrl = async () => {
    try { await api.post("/admin/ingestion/connectors/partner_feed", { config: { feed_url: feedUrl } }); toast.success("Feed URL saved"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

const PLAN_OPTS = [["", "Plan…"], ["per_listing", "Per Listing $50"], ["starter", "Starter $250"], ["growth", "Growth $500"], ["unlimited", "Unlimited $1,000"]];
const PAY_OPTS = [["", "Payment…"], ["free_trial", "Free trial"], ["payment_confirmed", "Payment confirmed"], ["unpaid", "Unpaid"]];

function MonetizationPanel({ listingId }) {
  const [m, setM] = useState({ plan: "", payment_status: "", payment_link: "", free_trial_start: "", free_trial_end: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setM((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/listings/${listingId}/monetization`, m); toast.success("Plan & payment tracking saved"); }
    catch (e) { toast.error(fmtErr(e)); }
    setSaving(false);
  };
  const cls = "rounded-lg border border-input bg-background px-2 py-1.5 text-xs";
  return (
    <div data-testid={`monetization-${listingId}`} className="w-full mt-3 rounded-xl bg-secondary/60 border border-border p-4">
      <p className="text-xs font-semibold mb-2">Plan & payment tracking (internal — never shown to renters)</p>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid={`mon-plan-${listingId}`} value={m.plan} onChange={(e) => set("plan", e.target.value)} className={cls} aria-label="Plan">
          {PLAN_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid={`mon-payment-${listingId}`} value={m.payment_status} onChange={(e) => set("payment_status", e.target.value)} className={cls} aria-label="Payment status">
          {PAY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid={`mon-link-${listingId}`} value={m.payment_link} onChange={(e) => set("payment_link", e.target.value)}
          placeholder="Stripe payment link (optional)" aria-label="Payment link" className={`${cls} w-52 font-mono`} />
        <label className="text-xs text-muted-foreground">Trial start <input data-testid={`mon-trial-start-${listingId}`} type="date" value={m.free_trial_start} onChange={(e) => set("free_trial_start", e.target.value)} className={cls} /></label>
        <label className="text-xs text-muted-foreground">Trial end <input data-testid={`mon-trial-end-${listingId}`} type="date" value={m.free_trial_end} onChange={(e) => set("free_trial_end", e.target.value)} className={cls} /></label>
        <button data-testid={`mon-save-${listingId}`} onClick={save} disabled={saving}
          className="rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold disabled:opacity-50">
          {saving ? "Saving…" : "Save tracking"}
        </button>
      </div>
    </div>
  );
}

  const run = async (name) => {
    setBusy(name);
    try {
      const r = await api.post(`/admin/ingestion/run/${name}`);
      if (r.data.blocked) toast.warning(r.data.reason);
      else toast.success(`Run complete: ${JSON.stringify(r.data)}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy("");
  };

  const toggleScraping = async (allowed) => {
    try { await api.post("/admin/ingestion/connectors/scraper", { scraping_allowed: allowed, enabled: allowed }); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const publishSub = async (listingId) => {
    try { await api.post(`/admin/listings/${listingId}/publish`, { published: true }); toast.success("Published — listing is now live"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const decide = async (id, action) => {
    try {
      if (action === "reject") {
        const reason = window.prompt("Reason for rejecting (optional):") || "";
        await api.post(`/admin/pm-submissions/${id}/reject`, { reason });
        toast.success("Submission rejected");
      } else {
        await api.post(`/admin/pm-submissions/${id}/approve`);
        toast.success("Listing approved — now live");
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="ingestion-page">
      <h1 className="font-heading font-bold text-2xl tracking-tight">Ingestion & connectors</h1>
      <p className="text-sm text-muted-foreground mb-6">Pluggable sources feeding the inventory engine. Everything is normalized, de-duplicated, and logged.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.connectors.map((c) => (
          <div key={c.name} data-testid={`connector-${c.name}`} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold flex items-center gap-2">{c.label}
                  {c.name === "scraper" && <ShieldAlert className="w-4 h-4 text-coral-500" />}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{c.description}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${c.enabled ? "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100" : "bg-secondary"}`}>{c.enabled ? "enabled" : "disabled"}</span>
            </div>
            {c.name === "scraper" && (
              <div className="mt-3 rounded-xl bg-coral-50 dark:bg-coral-800/20 border border-coral-100 dark:border-coral-800 p-3">
                <p className="text-xs text-coral-800 dark:text-coral-400 leading-relaxed">
                  Scraping is opt-in per source and defaults OFF. Each run checks robots.txt and this flag. You are responsible for ensuring any enabled source permits automated access.
                </p>
                {user?.role === "admin" && (
                  <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-coral-800 dark:text-coral-400 cursor-pointer">
                    <input data-testid="scraper-allowed-toggle" type="checkbox" checked={!!c.scraping_allowed} onChange={(e) => toggleScraping(e.target.checked)} className="accent-[#E55647]" />
                    I understand — allow scraping for configured sources
                  </label>
                )}
              </div>
            )}
            {(c.name === "partner_feed" || c.name === "scraper") && user?.role === "admin" && (
              <button data-testid={`run-${c.name}`} onClick={() => run(c.name)} disabled={busy === c.name}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold disabled:opacity-50">
                {busy === c.name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run now
              </button>
            )}
            {c.name === "partner_feed" && user?.role === "admin" && (
              <div className="mt-3 flex gap-2">
                <input data-testid="feed-url-input" value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)}
                  placeholder="https://partner.com/feed.json" aria-label="Partner feed URL"
                  className="flex-1 rounded-lg border border-input bg-background px-3 py-1.5 text-xs font-mono" />
                <button data-testid="feed-url-save" onClick={saveFeedUrl}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold">Save URL</button>
              </div>
            )}
            {c.name === "partner_feed" && !feedUrl && <p className="mt-2 text-[11px] text-muted-foreground">No feed configured yet — add a partner's JSON feed URL to enable pulls.</p>}
          </div>
        ))}
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-semibold">Stale listing check</h2>
          <p className="mt-1 text-xs text-muted-foreground">Listings not updated within the window flip to Expired and drop out of renter results, but stay in the archive.</p>
          <div className="mt-3 flex items-center gap-2">
            <input data-testid="expire-days-input" type="number" min="1" max="365" value={expireDays}
              onChange={(e) => setExpireDays(Number(e.target.value))} aria-label="Auto-expire window in days"
              className="w-20 rounded-lg border border-input bg-background px-2 py-1.5 text-xs" />
            <span className="text-xs text-muted-foreground">days</span>
            {user?.role === "admin" && (
              <button data-testid="expire-days-save" onClick={saveDays}
                className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold">Save window</button>
            )}
          </div>
          {user?.role === "admin" && (
            <button data-testid="run-stale-check" onClick={() => run("stale_check")} disabled={busy === "stale_check"}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold">
              {busy === "stale_check" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run now
            </button>
          )}
        </div>
      </div>

      <h2 className="mt-10 font-heading font-semibold text-lg">Submitted listings — pending review</h2>
      <div className="mt-3 space-y-3">
        {subs.map((s) => (
          <div key={s.id} data-testid={`pm-sub-${s.id}`} className="rounded-2xl border border-border bg-card p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">{s.address_street}{s.unit_number ? ` #${s.unit_number}` : ""} — {s.neighborhood}</p>
              <p className="text-xs text-muted-foreground">{s.company_name} · {s.contact_name} · {s.email} · ${s.monthly_rent}/mo · {s.beds} bed</p>
              {s.reject_reason && <p className="mt-1 text-xs text-coral-600">Rejected: {s.reject_reason}</p>}
              {s.merged_into_existing && <p className="mt-1 text-xs text-muted-foreground">Matched an existing listing record (duplicate merge).</p>}
            </div>
            {s.status === "pending" ? (
              <div className="flex flex-wrap gap-2">
                {s.listing_id && (
                  <Link to={`/dashboard/inventory/${s.listing_id}/edit`} data-testid={`pm-review-${s.id}`}
                    className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:bg-secondary">Review & edit</Link>
                )}
                <button data-testid={`pm-approve-${s.id}`} onClick={() => decide(s.id, "approve")} className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                {s.listing_id && (
                  <button data-testid={`pm-publish-${s.id}`} onClick={() => publishSub(s.listing_id)} className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground px-4 py-1.5 text-xs font-semibold"><Globe className="w-3.5 h-3.5" /> Publish</button>
                )}
                <button data-testid={`pm-reject-${s.id}`} onClick={() => decide(s.id, "reject")} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-1.5 text-xs font-semibold"><XCircle className="w-3.5 h-3.5" /> Reject</button>
              </div>
            ) : (
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold capitalize">{s.status}</span>
            )}
            {s.status === "pending" && s.listing_id && <MonetizationPanel listingId={s.listing_id} />}
          </div>
        ))}
        {!subs.length && <p data-testid="pm-subs-empty" className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No submissions yet. The form lives at /for-landlords.</p>}
      </div>

      <h2 className="mt-10 font-heading font-semibold text-lg">Recent runs</h2>
      <div className="mt-3 rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-5 py-3">Connector</th><th className="px-5 py-3">Started</th><th className="px-5 py-3">Added</th><th className="px-5 py-3">Updated</th><th className="px-5 py-3">Failed</th><th className="px-5 py-3">Log</th>
          </tr></thead>
          <tbody>
            {data.runs.map((r) => (
              <tr key={r.id} className="border-b border-border/60 last:border-0">
                <td className="px-5 py-2.5 font-medium">{r.connector}</td>
                <td className="px-5 py-2.5 text-xs text-muted-foreground">{r.started_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-5 py-2.5">{r.added}</td><td className="px-5 py-2.5">{r.updated}</td><td className="px-5 py-2.5">{r.failed}</td>
                <td className="px-5 py-2.5 text-xs text-muted-foreground max-w-[280px] truncate" title={r.log}>{r.log}</td>
              </tr>
            ))}
            {!data.runs.length && <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground">No runs logged yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
